// Keys and scales for the locked editor and the validator (key lock, SPEC §3.4). Pitch classes
// are 0–11 (C = 0). Only scale helpers live here; the Wizard's theory stays on the server.

export type Mode = "major" | "minor";
export interface Key {
  tonic: number;
  mode: Mode;
}

export const NOTE_NAMES = ["C", "C#", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];

export const MODE_STEPS: Record<Mode, readonly number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
};

export const pitchClass = (n: number) => ((n % 12) + 12) % 12;

export function keyName(k: Key): string {
  return `${NOTE_NAMES[pitchClass(k.tonic)]} ${k.mode}`;
}

/** The relative key: the same seven notes with another home (C major ↔ A minor). */
export function relativeKey(k: Key): Key {
  return k.mode === "major"
    ? { tonic: pitchClass(k.tonic + 9), mode: "minor" }
    : { tonic: pitchClass(k.tonic + 3), mode: "major" };
}

/** A day's two keys: its own and the relative one (a song picks either, `LoopData.alt_key`). */
export function dayKeys(k: Key): [Key, Key] {
  return [k, relativeKey(k)];
}

/** The key a song is in: the day's key, or its relative when the song chose the second key. */
export function songKey(k: Key, loop?: { alt_key?: boolean } | null): Key {
  return loop?.alt_key ? relativeKey(k) : k;
}

/** "C major / A minor": a day's two keys. */
export function keyPairName(k: Key): string {
  return `${keyName(k)} / ${keyName(relativeKey(k))}`;
}

export function keyShort(k: Key): string {
  return `${NOTE_NAMES[pitchClass(k.tonic)]}${k.mode === "minor" ? "m" : ""}`;
}

export function scalePitchClasses(k: Key): number[] {
  return MODE_STEPS[k.mode].map((s) => pitchClass(k.tonic + s));
}

export function inKey(note: number, k: Key): boolean {
  return scalePitchClasses(k).includes(pitchClass(note));
}

/** Nearest in-key note (ties go down). */
export function snapToKey(note: number, k: Key): number {
  for (let d = 0; d < 7; d++) {
    if (inKey(note - d, k)) return note - d;
    if (inKey(note + d, k)) return note + d;
  }
  return note;
}

/** In-key notes in [lo, hi], ascending. */
export function scaleNotesInRange(k: Key, lo: number, hi: number): number[] {
  const out: number[] = [];
  for (let n = lo; n <= hi; n++) if (inKey(n, k)) out.push(n);
  return out;
}

/** Degree (0-based, may exceed 6 across octaves) of an in-key note relative to the tonic octave. */
export function noteName(n: number): string {
  return `${NOTE_NAMES[pitchClass(n)]}${Math.floor(n / 12) - 1}`;
}

export function isTonic(note: number, k: Key): boolean {
  return pitchClass(note) === pitchClass(k.tonic);
}
